import { useState } from "react";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { IconLineDownload } from "@kalodata/kds-design-icons";
import { DownloadVideo } from "@components/DownloadVideo";
import { IconVideoEmpty } from "@icons/common";
import type { JobDetailData } from "@apis/videoCreatingService";
import type { UserAssetV2Record } from "@apis/http/api";
import { DetailGeneratingView } from "./DetailGeneratingView";
import { DetailVideoPlayer } from "./DetailVideoPlayer";
import { PlaySimultaneouslyBar } from "./PlaySimultaneouslyBar";
import { VideoCompareColumn } from "./VideoCompareColumn";
import { getSourceVideo, getVideoUrl } from "./helpers";

interface RemakeCompareViewProps {
  jobDetail: JobDetailData | null;
  record: UserAssetV2Record;
  aspectRatio?: string;
  suspended?: boolean;
}

const DownloadIconBtn = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center justify-center p-[6px] rounded-[4px] bg-kds-bg-overlay hover:bg-kds-bg-element transition-colors cursor-pointer border-0"
  >
    <IconLineDownload size={20} className="text-kds-fg" />
  </button>
);

export const RemakeCompareView = ({ jobDetail, record, aspectRatio, suspended = false }: RemakeCompareViewProps) => {
  const [playCommand, setPlayCommand] = useState(0);
  const [pauseCommand, setPauseCommand] = useState(0);
  const [sourcePlaying, setSourcePlaying] = useState(false);
  const [newPlaying, setNewPlaying] = useState(false);

  const source = getSourceVideo(jobDetail);
  const newVideoUrl = getVideoUrl(jobDetail, record);
  const status = record?.jobItem?.status;
  const isProcessing = status === "processing" || status === "queued";

  const anyPlaying = sourcePlaying || newPlaying;

  const handleToggleSimultaneous = () => {
    if (anyPlaying) {
      setPauseCommand((n) => n + 1);
    } else {
      setPlayCommand((n) => n + 1);
    }
  };

  const handleSourceDownload = () => {
    if (!source.url) return;
    const link = document.createElement("a");
    link.href = `${source.url}?download=1`;
    link.download = "source-video.mp4";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const renderNewVideo = () => {
    if (isProcessing) return <DetailGeneratingView aspectRatio={aspectRatio} record={record} />;
    if (newVideoUrl) {
      return (
        <DetailVideoPlayer
          key={newVideoUrl}
          playerId="detail-new-player"
          videoURL={newVideoUrl}
          aspectRatio={aspectRatio}
          suspended={suspended}
          showHint={false}
          autoplay={false}
          playCommand={playCommand}
          pauseCommand={pauseCommand}
          onPlayingChange={setNewPlaying}
        />
      );
    }
    return (
      <div className="flex flex-col items-center justify-center text-kds-fg-subtle max-w-[400px] text-center">
        <IconVideoEmpty className="w-[100px] h-[100px]" />
        <span className="text-[16px] font-medium text-kds-fg mt-[8px]">
          <Trans>Couldn't generate video</Trans>
        </span>
        {jobDetail?.errorMessage && (
          <span className="text-[14px] text-kds-fg-soft mt-[4px]">{t(jobDetail.errorMessage)}</span>
        )}
      </div>
    );
  };

  const renderSourceVideo = () => {
    if (source.url) {
      return (
        <DetailVideoPlayer
          key={source.url}
          playerId="detail-source-player"
          videoURL={source.url}
          aspectRatio={aspectRatio}
          suspended={suspended}
          showHint={false}
          autoplay={false}
          playCommand={playCommand}
          pauseCommand={pauseCommand}
          onPlayingChange={setSourcePlaying}
        />
      );
    }
    if (source.cover) {
      return (
        <div className="h-full flex items-center justify-center overflow-hidden">
          <img src={source.cover} alt="source" className="h-full max-w-full object-contain rounded-[4px]" />
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center text-kds-fg-subtle text-center">
        <IconVideoEmpty className="w-[100px] h-[100px]" />
      </div>
    );
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex-1 min-h-0 flex items-stretch">
        {/* Original Video */}
        <VideoCompareColumn
          className="border-l"
          title={<Trans>Original Video</Trans>}
          titleClassName="pl-[56px]"
          action={source.url ? <DownloadIconBtn onClick={handleSourceDownload} /> : undefined}
        >
          {renderSourceVideo()}
        </VideoCompareColumn>

        {/* New Video */}
        <VideoCompareColumn
          title={<Trans>New Video</Trans>}
          action={
            newVideoUrl ? (
              <DownloadVideo videoURL={newVideoUrl} videoId={String(record.id)} kalo_type="remake_detail">
                {(isDownloading) => (
                  <span
                    className="flex items-center justify-center p-[6px] rounded-[4px] bg-kds-bg-overlay hover:bg-kds-bg-element transition-colors cursor-pointer"
                    aria-busy={isDownloading}
                  >
                    <IconLineDownload size={20} className="text-kds-fg" />
                  </span>
                )}
              </DownloadVideo>
            ) : undefined
          }
        >
          {renderNewVideo()}
        </VideoCompareColumn>
      </div>

      {/* Play Simultaneously */}
      {source.url && newVideoUrl && (
        <PlaySimultaneouslyBar isPlaying={anyPlaying} onToggle={handleToggleSimultaneous} />
      )}
    </div>
  );
};
